import type { EditorSaveToastContext } from "../../utils/editor";
import {
  buildSavePersistedEventDetail,
  type SaveSseActiveEntryLike,
  type SaveSseTrackedJobLike,
} from "./saveSse";

/**
 * Unity `OperationHandler.IsUserConfirmedProjectSave()` 의 6개 reason 정본 미러.
 *
 * 정본: `apps/web/mw_unity/Assets/Scripts/05.ViewRReactBridge/Handlers/OperationHandler.cs`
 * (`ManualSaveReason` … `SpaceDuplicateBeforeSaveReason`)
 * SSOT: STUDIO-PACKAGE-003 "구현 직전 reason registry 보완" —
 *   사용자가 Save 를 선택한 앞의 여섯 reason 이 Project thumbnail 을 수집합니다.
 *   (`xr_entry_point_autosave` 는 thumbnail 을 갱신하지 않으므로 제외)
 *
 * Bridge 를 넘는 문자열이라 Unity/React 양쪽 정의가 불가피하게 중복됩니다.
 * 목록을 바꿀 때는 위 Unity 정본을 먼저 고치세요.
 */
export const USER_CONFIRMED_PROJECT_SAVE_REASONS = [
  "manual_save",
  "manual_topbar_save",
  "navigate_home_before_save",
  "space_switch_before_save",
  "add_space_before_save",
  "space_duplicate_before_save",
] as const;

/** 사용자가 Save 를 직접 선택해 Unity 가 Project thumbnail 을 새로 캡처하는 저장인지. */
export const isUserConfirmedProjectSaveReason = (
  reason?: string,
): boolean =>
  USER_CONFIRMED_PROJECT_SAVE_REASONS.some((known) => known === reason);

/**
 * 상단/단축키 Save 전용 판정.
 *
 * 주의: 이 predicate 는 `shouldSkipThumbnailAfterPersist` 와 save dedup(`isManualSave`)의
 * 저장 동작을 결정합니다. 진단 로그 범위를 넓히려고 여기에 reason 을 추가하면
 * 저장 완료 popup·중복 저장 판정까지 함께 바뀝니다.
 * 관측 목적이라면 `isUserConfirmedProjectSaveReason` 을 사용하세요.
 */
export const isManualProjectThumbnailSaveReason = (
  reason?: string,
): boolean => reason === "manual_save" || reason === "manual_topbar_save";

/**
 * 프로젝트 썸네일 추적 로그를 남길지. 저장 동작에는 영향을 주지 않습니다.
 *
 * 기존 게이트(`skipThumbnailAfterPersist !== true`)는 상단 Save 2개 reason 에서만
 * 참이라 Home 이동·Space 전환·Space 추가·복제 전 Save 처럼 실제로 Unity 가
 * thumbnail 을 새로 캡처하는 저장에서 로그가 비어 있었습니다.
 */
export const shouldTraceProjectThumbnailPersist = ({
  skipThumbnailAfterPersist,
  reason,
}: {
  skipThumbnailAfterPersist?: boolean;
  reason?: string;
}): boolean =>
  skipThumbnailAfterPersist !== true ||
  isUserConfirmedProjectSaveReason(reason);

export const shouldSkipThumbnailAfterPersist = (
  saveContext: EditorSaveToastContext | undefined,
  reason?: string,
): boolean =>
  saveContext === "spaceSwitch" ||
  saveContext === "spaceSwitchModal" ||
  !isManualProjectThumbnailSaveReason(reason);

export const buildPersistedSaveEventDetail = ({
  gcsPath,
  jobId,
  source,
  suppressToast,
  saveContext,
  saveAttemptId,
  reason,
  originMutationId,
  signature,
}: {
  gcsPath: string;
  jobId?: string;
  source: string;
  suppressToast?: boolean;
  saveContext?: EditorSaveToastContext;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
  signature?: string;
}) =>
  buildSavePersistedEventDetail({
    gcsPath,
    jobId,
    source,
    suppressToast,
    saveAttemptId,
    reason,
    originMutationId,
    signature,
    skipThumbnailAfterPersist:
      saveContext !== undefined
        ? shouldSkipThumbnailAfterPersist(saveContext, reason)
        : undefined,
  });

export const resolvePersistedSaveUiState = ({
  trackedJob,
  activeEntryBeforeRelease,
}: {
  trackedJob?: SaveSseTrackedJobLike;
  activeEntryBeforeRelease?: SaveSseActiveEntryLike;
}): {
  suppressSaveSyncedToastResolved: boolean;
  saveContext: EditorSaveToastContext;
  pathForRelease?: string;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
} => ({
  suppressSaveSyncedToastResolved:
    trackedJob !== undefined
      ? trackedJob.suppressSaveSyncedToast
      : activeEntryBeforeRelease?.suppressSaveSyncedToast === true,
  saveContext:
    trackedJob?.saveContext ?? activeEntryBeforeRelease?.saveContext ?? "default",
  pathForRelease: trackedJob?.path,
  saveAttemptId: trackedJob?.saveAttemptId ?? activeEntryBeforeRelease?.saveAttemptId,
  reason: trackedJob?.reason ?? activeEntryBeforeRelease?.reason,
  originMutationId:
    trackedJob?.originMutationId ?? activeEntryBeforeRelease?.originMutationId,
});
